import React, { useState, useEffect, useRef } from "react";
import {
  Loader,
  Grid,
  Card,
  Text,
  Group,
  Stack,
  Box,
  TextInput,
  SegmentedControl,
  Paper,
  Table,
  Badge,
  Button,
  Avatar,
  Textarea,
  Title,
  ActionIcon,
  Divider,
  Transition,
} from "@mantine/core";
import {
  IconSearch,
  IconRefresh,
  IconVideo,
  IconCheck,
  IconX,
  IconClock,
  IconPlayerPlay,
  IconChevronLeft,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";

/* ─── helpers ─── */
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const STATUS_LABELS = {
  pending: "Pending Review",
  approved: "Approved",
  needs_work: "Resubmit",
  resubmit: "Resubmit",
};

const STATUS_COLORS = {
  pending: "orange",
  approved: "teal",
  needs_work: "red",
  resubmit: "red",
};

/* ─── half-star rating ─── */
function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const display = hover || value;

  const handleMouseMove = (e, n) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const isLeft = e.clientX - rect.left < rect.width / 2;
    setHover(isLeft ? n - 0.5 : n);
  };

  const handleClick = (e, n) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const isLeft = e.clientX - rect.left < rect.width / 2;
    const newVal = isLeft ? n - 0.5 : n;
    onChange(value === newVal ? 0 : newVal);
  };

  return (
    <Group gap={4} align="center">
      {[1, 2, 3, 4, 5].map(n => {
        const filled = Math.min(Math.max(display - (n - 1), 0), 1);
        return (
          <Box
            key={n}
            style={{ position: "relative", cursor: "pointer", fontSize: 30 }}
            onMouseMove={e => handleMouseMove(e, n)}
            onMouseLeave={() => setHover(0)}
            onClick={e => handleClick(e, n)}
          >
            <span style={{ color: "#dee2e6" }}>★</span>
            {filled > 0 && (
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  color: "#f59f00",
                  clipPath: filled >= 1 ? "none" : "inset(0 50% 0 0)",
                  pointerEvents: "none",
                }}
              >
                ★
              </span>
            )}
          </Box>
        );
      })}
      <Text size="sm" c="dimmed" mt={2} ml={4}>
        {value > 0 ? `${value}/5` : "No rating"}
      </Text>
    </Group>
  );
}

/* ════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════ */
export default function PitchReviewPanel() {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("pending");

  // Inline review panel state (replaces stat cards when open)
  const [selected, setSelected] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const videoRef = useRef(null);

  /* ─── fetch ─── */
  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const res = await fetch(
        getApiEndpoint("/v1/pitch-review/manager/submissions"),
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) {
        setError("Failed to load pitch submissions");
        return;
      }
      const data = await res.json();
      setSubmissions(data.data || []);
    } catch {
      setError("Failed to load pitch submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions(); /* eslint-disable-next-line */
  }, []);

  /* ─── open inline review panel ─── */
  const handleSelect = sub => {
    setSelected(sub);
    setApprovalStatus(
      sub.approval_status && sub.approval_status !== "pending"
        ? sub.approval_status === "needs_work"
          ? "resubmit"
          : sub.approval_status
        : null
    );
    setScore(sub.score || 0);
    setFeedback(sub.manager_feedback || "");

    // Scroll to top of page so the review panel is immediately visible
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Let React re-render before trying to reload video
    setTimeout(() => {
      if (videoRef.current) videoRef.current.load();
    }, 50);
  };

  /* ─── close inline review panel ─── */
  const handleClose = () => {
    setSelected(null);
    setApprovalStatus(null);
    setScore(0);
    setFeedback("");
  };

  /* ─── submit review ─── */
  const handleSubmitReview = async () => {
    if (!selected || !approvalStatus) return;
    const statusMap = { approve: "approved", resubmit: "resubmit" };
    const mappedStatus = statusMap[approvalStatus] || approvalStatus;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        showError("Authentication failed");
        return;
      }

      const res = await fetch(
        getApiEndpoint(
          `/v1/pitch-review/message/${selected.message_id}/review`
        ),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            approval_status: mappedStatus,
            score: score || null,
            feedback: feedback.trim() || null,
            program_user_completed_task_id:
              selected.program_user_completed_task_id || null,
          }),
        }
      );
      if (!res.ok) {
        showError("Failed to save review");
        return;
      }

      showSuccess("Review saved!");
      setSubmissions(prev =>
        prev.map(s =>
          s.message_id === selected.message_id
            ? {
                ...s,
                approval_status: mappedStatus,
                score: score || null,
                manager_feedback: feedback.trim() || null,
              }
            : s
        )
      );
      // Collapse back to stat cards
      handleClose();
    } catch {
      showError("Failed to save review");
    } finally {
      setSubmitting(false);
    }
  };

  /* ─── filter ─── */
  const filtered = (() => {
    const base = submissions.filter(s => {
      const matchSearch =
        !search.trim() ||
        s.rep_name.toLowerCase().includes(search.toLowerCase());
      const matchStatus =
        filterStatus === "all" ||
        (filterStatus === "pending" &&
          (s.approval_status === "pending" || !s.approval_status)) ||
        (filterStatus === "approved" && s.approval_status === "approved");
      return matchSearch && matchStatus;
    });

    // Float the selected row to the top of the list
    if (!selected) return base;
    const idx = base.findIndex(s => s.message_id === selected.message_id);
    if (idx <= 0) return base;
    const reordered = [...base];
    reordered.splice(idx, 1);
    reordered.unshift(base[idx]);
    return reordered;
  })();

  const pendingCount = submissions.filter(
    s => s.approval_status === "pending" || !s.approval_status
  ).length;
  const approvedCount = submissions.filter(
    s => s.approval_status === "approved"
  ).length;
  const needsWorkCount = submissions.filter(
    s => s.approval_status === "needs_work" || s.approval_status === "resubmit"
  ).length;

  const metricCards = [
    {
      label: "Total Submissions",
      value: submissions.length,
      icon: IconVideo,
      iconColor: "#228be6",
      iconBg: "#e7f5ff",
    },
    {
      label: "Pending Review",
      value: pendingCount,
      icon: IconClock,
      iconColor: "#f08c00",
      iconBg: "#fff4e6",
    },
    {
      label: "Approved",
      value: approvedCount,
      icon: IconCheck,
      iconColor: "#2f9e44",
      iconBg: "#ebfbee",
    },
    {
      label: "Needs Work",
      value: needsWorkCount,
      icon: IconX,
      iconColor: "#e03131",
      iconBg: "#fff5f5",
    },
  ];

  /* ────────────────────────────────────────
     TOP SECTION: stat cards OR inline review
  ──────────────────────────────────────── */
  const topSection = selected ? (
    /* ── Inline Review Panel — animated open ── */
    <Transition
      mounted={!!selected}
      transition="slide-down"
      duration={200}
      timingFunction="cubic-bezier(0.22, 1, 0.36, 1)"
    >
      {styles => (
        <Paper
          withBorder
          radius="lg"
          p={0}
          style={{ ...styles, overflow: "hidden", border: "1px solid #e9ecef" }}
        >
          {/* Header bar */}
          <Group
            justify="space-between"
            align="center"
            px="lg"
            py="md"
            style={{ borderBottom: "1px solid #e9ecef", background: "#fafbfc" }}
          >
            <Group gap="sm">
              <ActionIcon
                variant="subtle"
                color="gray"
                radius="xl"
                onClick={handleClose}
                title="Back to overview"
              >
                <IconChevronLeft size={18} />
              </ActionIcon>
              <Avatar size="md" radius="xl" color="orange">
                {initials(selected.rep_name)}
              </Avatar>
              <Box>
                <Text fw={700} size="md">
                  {selected.rep_name}
                </Text>
                <Text size="sm" c="dimmed">
                  {selected.source_label ||
                    (selected.video_title
                      ? `Course Video: ${selected.video_title}`
                      : "General Pitch")}
                  {selected.submitted_at &&
                    ` · ${timeAgo(selected.submitted_at)}`}
                  {selected.duration_seconds != null &&
                    ` · ${Math.floor(selected.duration_seconds / 60)}:${String(
                      selected.duration_seconds % 60
                    ).padStart(2, "0")}`}
                </Text>
              </Box>
            </Group>
            <ActionIcon
              variant="subtle"
              color="gray"
              radius="xl"
              onClick={handleClose}
              title="Close"
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>

          {/* Body: left = video, right = review form */}
          <Grid gutter={0} style={{ minHeight: 360 }}>
            {/* LEFT: Video player */}
            <Grid.Col
              span={{ base: 12, md: 7 }}
              style={{ borderRight: "1px solid #e9ecef" }}
            >
              <Box
                p="lg"
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  minHeight: 340,
                }}
              >
                {selected.response_url ? (
                  <Box
                    style={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      borderRadius: 10,
                      overflow: "hidden",
                      background: "#000",
                    }}
                  >
                    <video
                      ref={videoRef}
                      controls
                      controlsList="nodownload"
                      key={selected.response_url}
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        objectFit: "contain",
                      }}
                    >
                      <source src={selected.response_url} type="video/mp4" />
                    </video>
                  </Box>
                ) : (
                  <Box
                    style={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      background: "#f1f3f5",
                      borderRadius: 10,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                    }}
                  >
                    <IconVideo size={40} color="#868e96" />
                    <Text size="sm" c="dimmed">
                      No video available
                    </Text>
                  </Box>
                )}
              </Box>
            </Grid.Col>

            {/* RIGHT: Review form */}
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Stack gap="lg" p="lg" h="100%">
                {/* Rep note — always shown */}
                <Box>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
                    Note from Rep
                  </Text>
                  {selected.response_text ? (
                    <Text size="sm" style={{ fontStyle: "italic" }}>
                      "{selected.response_text}"
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed" style={{ fontStyle: "italic" }}>
                      No notes
                    </Text>
                  )}
                  <Divider mt="md" />
                </Box>

                {/* Approve / Resubmit */}
                <Box>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={8}>
                    Decision
                  </Text>
                  <Group gap="sm">
                    <Button
                      size="sm"
                      variant={
                        approvalStatus === "approve" ? "filled" : "light"
                      }
                      color="teal"
                      leftSection={<IconCheck size={14} />}
                      onClick={() =>
                        setApprovalStatus(
                          approvalStatus === "approve" ? null : "approve"
                        )
                      }
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant={
                        approvalStatus === "resubmit" ? "filled" : "light"
                      }
                      color="red"
                      leftSection={<IconX size={14} />}
                      onClick={() =>
                        setApprovalStatus(
                          approvalStatus === "resubmit" ? null : "resubmit"
                        )
                      }
                    >
                      Request Resubmit
                    </Button>
                  </Group>
                </Box>

                {/* Star rating */}
                <Box>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={8}>
                    Score (out of 5)
                  </Text>
                  <StarRating value={score} onChange={setScore} />
                </Box>

                {/* Feedback */}
                <Textarea
                  label={
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                      Feedback
                    </Text>
                  }
                  placeholder="Provide constructive feedback for the rep..."
                  value={feedback}
                  onChange={e => setFeedback(e.currentTarget.value)}
                  minRows={4}
                  autosize
                  style={{ flex: 1 }}
                />

                {/* Submit */}
                <Group justify="flex-end" mt="auto">
                  <Button
                    variant="light"
                    color="gray"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!approvalStatus || submitting}
                    leftSection={
                      submitting ? (
                        <Loader size="xs" color="white" />
                      ) : (
                        <IconCheck size={16} />
                      )
                    }
                    onClick={handleSubmitReview}
                  >
                    Submit Review
                  </Button>
                </Group>
              </Stack>
            </Grid.Col>
          </Grid>
        </Paper>
      )}
    </Transition>
  ) : (
    /* ── Stat cards ── */
    <Grid gutter="xs">
      {metricCards.map(({ label, value, icon: Icon, iconColor, iconBg }) => (
        <Grid.Col key={label} span={{ base: 12, sm: 6, lg: 3 }}>
          <Card
            padding="xl"
            radius="lg"
            withBorder
            h="100%"
            style={{ border: "1px solid #e9ecef" }}
          >
            <Stack
              gap="lg"
              align="center"
              justify="space-between"
              h="100%"
              style={{ textAlign: "center" }}
            >
              <Box
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                }}
              >
                <Icon size={18} color={iconColor} />
              </Box>
              <Text size="md" c="dimmed" fw={600} ta="center" lineClamp={1}>
                {label}
              </Text>
              <Text
                fw={800}
                ta="center"
                style={{ fontSize: 28, lineHeight: 1.1, marginTop: 6 }}
              >
                {value}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      ))}
    </Grid>
  );

  /* ─── render ─── */
  return (
    <Stack gap="lg">
      {/* Top section swaps between stat cards and inline review panel */}
      {topSection}

      {/* ── Submissions Table ── */}
      <Paper withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
        <Stack gap={0}>
          <Group justify="space-between" align="center" p="lg" pb="md">
            <Title order={3}>Pitch Submissions</Title>
            <Group gap="sm" wrap="wrap">
              <TextInput
                placeholder="Search by rep name..."
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={e => setSearch(e.currentTarget.value)}
                style={{ minWidth: 220 }}
              />
              <SegmentedControl
                value={filterStatus}
                onChange={setFilterStatus}
                data={[
                  { label: "Pending", value: "pending" },
                  { label: "Approved", value: "approved" },
                  { label: "All", value: "all" },
                ]}
              />
              <Button
                variant="light"
                color="gray"
                leftSection={<IconRefresh size={14} />}
                onClick={fetchSubmissions}
                loading={loading}
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {loading ? (
            <Group justify="center" py="xl">
              <Loader size="md" />
              <Text c="dimmed">Loading submissions...</Text>
            </Group>
          ) : error ? (
            <Group justify="center" py="xl" gap="sm">
              <Text c="red">{error}</Text>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={fetchSubmissions}
              >
                Retry
              </Button>
            </Group>
          ) : (
            <Box style={{ overflowX: "auto" }}>
              <Table
                highlightOnHover
                verticalSpacing="md"
                horizontalSpacing="lg"
              >
                <Table.Thead>
                  <Table.Tr>
                    {[
                      "Rep",
                      "Video / Submission",
                      "Submitted",
                      "Status",
                      "Score",
                      "Actions",
                    ].map(h => (
                      <Table.Th
                        key={h}
                        style={{
                          background: "#fafbfc",
                          borderTop: "1px solid #e5e7eb",
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        {h}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filtered.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Text size="sm" c="dimmed" ta="center" py="md">
                          {submissions.length === 0
                            ? "No pitch submissions yet"
                            : "No matches found"}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    filtered.map(sub => {
                      const status = sub.approval_status || "pending";
                      const sourceLabel =
                        sub.source_label ||
                        (sub.video_title
                          ? `Course Video: ${sub.video_title}`
                          : "General Pitch");
                      const isActive = selected?.message_id === sub.message_id;

                      return (
                        <Table.Tr
                          key={sub.message_id}
                          style={{
                            background: isActive ? "#fff8f0" : undefined,
                            outline: isActive ? "2px solid #f97316" : undefined,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            isActive ? handleClose() : handleSelect(sub)
                          }
                        >
                          {/* Rep */}
                          <Table.Td>
                            <Group gap="md" wrap="nowrap">
                              <Avatar size="lg" radius="xl" color="orange">
                                {initials(sub.rep_name)}
                              </Avatar>
                              <Text fw={600} size="md">
                                {sub.rep_name}
                              </Text>
                            </Group>
                          </Table.Td>
                          {/* Video / Submission */}
                          <Table.Td>
                            <Stack gap={2}>
                              <Text fw={600} size="md" lineClamp={1}>
                                {sub.video_title || "General Pitch"}
                              </Text>
                              <Text size="sm" c="dimmed" lineClamp={1}>
                                {sourceLabel}
                              </Text>
                            </Stack>
                          </Table.Td>
                          {/* Submitted */}
                          <Table.Td>
                            <Text size="md">{timeAgo(sub.submitted_at)}</Text>
                          </Table.Td>
                          {/* Status */}
                          <Table.Td>
                            <Badge
                              variant="light"
                              color={STATUS_COLORS[status] || "gray"}
                            >
                              {STATUS_LABELS[status] || status}
                            </Badge>
                          </Table.Td>
                          {/* Score */}
                          <Table.Td>
                            <Text size="md" fw={600}>
                              {sub.score != null ? `${sub.score}/5` : "—"}
                            </Text>
                          </Table.Td>
                          {/* Actions */}
                          <Table.Td>
                            <Button
                              size="xs"
                              variant={isActive ? "filled" : "light"}
                              color={isActive ? "orange" : undefined}
                              leftSection={<IconPlayerPlay size={12} />}
                              onClick={e => {
                                e.stopPropagation();
                                isActive ? handleClose() : handleSelect(sub);
                              }}
                            >
                              {isActive ? "Close" : "Review"}
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  )}
                </Table.Tbody>
              </Table>
            </Box>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
